import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, IconButton, Tooltip } from "@material-ui/core";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import FormatAlignJustifyIcon from "@material-ui/icons/FormatAlignJustify";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteIcon from "@material-ui/icons/Delete";
import ControlPointDuplicateIcon from "@material-ui/icons/ControlPointDuplicate";

import { SERVER_URL } from "../../../../utils";
import RgbaColorPickerPopover from "./RgbaColorPickerPopover";
import { FONT_OPTIONS } from "./escapeRoomEditorUiConstants";
import { applyTextColorToElement, saveTextSelection } from "./escapeRoomInlineTextFormat";

function QuizPreviewThumbnail({ quiz }) {
	const firstQ = (quiz?.questions || [])[0];
	const isMatch = firstQ?.type === "match";
	const isFillBlanks = firstQ?.type === "fillBlanks";
	const isTextAnswer = firstQ?.type === "textAnswer";
	const opts = firstQ?.options || [];
	const isImageType = firstQ?.type === "imageChoice" || firstQ?.type === "optionsWithImages";
	const resolveUrl = (url) => {
		if (!url || !url.trim()) return null;
		if (url.startsWith("http") || url.startsWith("/") || url.startsWith("data:")) return url;
		return `${SERVER_URL}/${url}`;
	};
	return (
		<Box style={{ width: "100%", padding: 6, background: "#f8fafc", borderRadius: 8 }}>
			<Box fontSize={10} fontWeight={600} color="#334155" mb={1} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(firstQ?.text || "Pregunta").slice(0, 25)}{(firstQ?.text || "").length > 25 ? "…" : ""}</Box>
			{isMatch ? (
				<Box fontSize={9} color="#64748b">Emparejar • {(firstQ?.matchLeft || []).length}↔{(firstQ?.matchRight || []).length}</Box>
			) : isFillBlanks ? (
				<Box fontSize={9} color="#64748b">Completar huecos</Box>
			) : isTextAnswer ? (
				<Box fontSize={9} color="#64748b">Respuesta escrita</Box>
			) : isImageType && opts.length > 0 ? (
				<Box display="grid" gridTemplateColumns={`repeat(${Math.min(opts.length, 3)}, 1fr)`} style={{ gap: 4 }}>
					{opts.slice(0, 6).map((o, i) => {
						const src = resolveUrl(o.imageUrl);
						return (
							<Box key={i} style={{ aspectRatio: "1", borderRadius: 6, overflow: "hidden", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
								{src ? (
									<img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
								) : (
									<Box fontSize={9} color="#94a3b8">{o.text?.slice(0, 4) || "?"}</Box>
								)}
							</Box>
						);
					})}
				</Box>
			) : (
				<Box display="flex" flexDirection="column" style={{ gap: 2 }}>
					{(opts.length ? opts : [{ text: "Opción 1" }]).slice(0, 3).map((o, i) => (
						<Box key={i} fontSize={9} color="#64748b" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>• {(o.text || "Opción").slice(0, 20)}{(o.text || "").length > 20 ? "…" : ""}</Box>
					))}
				</Box>
			)}
		</Box>
	);
}

/** Número entero: el valor guardado solo al salir del campo (evita que al borrar un dígito salte al mínimo). */
function DraftIntTextField({ value, onCommit, min, max, label, fullWidth, size, helperText, style, inputProps, variant, allowNegative }) {
	const [draft, setDraft] = useState(null);
	const v = Number(value);
	const base = Number.isFinite(v) ? Math.round(v) : min;
	useEffect(() => {
		setDraft(null);
	}, [value]);
	const display = draft !== null ? draft : String(base);
	const onCh = (e) => {
		const raw = e.target.value;
		if (allowNegative) {
			if (raw === "" || raw === "-") {
				setDraft(raw);
				return;
			}
			const t = raw.replace(/[^\d-]/g, "").replace(/(?!^)-/g, "");
			setDraft(t);
		} else {
			setDraft(raw.replace(/[^\d]/g, ""));
		}
	};
	const onBl = () => {
		const t = draft;
		setDraft(null);
		if (t === null || t === "" || t === "-") return;
		const p = parseInt(t, 10);
		if (Number.isNaN(p)) return;
		onCommit(Math.min(max, Math.max(min, p)));
	};
	const mergedInputProps = {
		...(inputProps || {}),
		onKeyDown: (e) => {
			if (typeof inputProps?.onKeyDown === "function") inputProps.onKeyDown(e);
			if (e.key === "Enter") {
				e.preventDefault();
				e.stopPropagation();
				e.target.blur();
			}
		},
	};
	return (
		<TextField
			label={label}
			type="text"
			inputMode={allowNegative ? "text" : "numeric"}
			size={size || "small"}
			fullWidth={fullWidth}
			variant={variant || "outlined"}
			style={style}
			helperText={helperText}
			value={display}
			onFocus={() => setDraft(allowNegative ? String(base) : String(base))}
			onChange={onCh}
			onBlur={onBl}
			inputProps={mergedInputProps}
		/>
	);
}

function stopToolbarEvent(e) {
	e.stopPropagation();
}

function StyleFloatingToolbarBase({
	anchor,
	values,
	onPatch,
	colorValue,
	onColorChange,
	onColorPickerPointerDown,
	fontOptions,
	onDuplicate,
	showDuplicate = true,
	defaultFontSize = 18,
}) {
	const s = values || {};
	const [fontSizeDraft, setFontSizeDraft] = useState(null);
	const fsBase = Math.max(8, Math.min(500, Math.round(Number(s.fontSize) || defaultFontSize)));
	useEffect(() => {
		setFontSizeDraft(null);
	}, [anchor?.elementId, s.fontSize]);
	const toolbarW = 560;
	const toolbarH = 44;
	const gap = 8;
	const left = Math.max(8, (anchor?.x || 0) + (anchor?.width || 200) / 2 - toolbarW / 2);
	const elY = anchor?.y ?? 0;
	const elH = anchor?.height ?? 80;
	const topAbove = elY - toolbarH - gap;
	const top = topAbove >= gap ? topAbove : elY + elH + gap;

	const tbBtn = (onClick, Icon, active) => (
		<IconButton
			data-er-editor-ui="1"
			size="small"
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
			style={{ padding: 5 }}
			onMouseDown={stopToolbarEvent}
			onPointerDown={stopToolbarEvent}
		>
			<Icon style={{ fontSize: 20, color: active ? "#1e293b" : "#64748b" }} />
		</IconButton>
	);

	const sep = () => (
		<Box
			style={{
				width: 1,
				minWidth: 1,
				maxWidth: 1,
				height: 20,
				margin: "0 2px",
				flexShrink: 0,
				background: "#e5e7eb",
			}}
		/>
	);

	return (
		<Box
			data-er-editor-ui="1"
			onPointerDown={stopToolbarEvent}
			onMouseDown={stopToolbarEvent}
			onClick={stopToolbarEvent}
			style={{
				position: "absolute",
				left,
				top,
				width: toolbarW,
				height: toolbarH,
				display: "flex",
				alignItems: "center",
				gap: 4,
				padding: "6px 10px",
				background: "#fff",
				borderRadius: 10,
				boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
				border: "1px solid #e5e7eb",
				zIndex: 100,
				boxSizing: "border-box",
				overflow: "visible",
				flexWrap: "nowrap",
				pointerEvents: "auto",
			}}
		>
			<select
				data-er-editor-ui="1"
				value={s.fontFamily || "Arial, sans-serif"}
				onChange={(e) => onPatch({ fontFamily: e.target.value })}
				onMouseDown={stopToolbarEvent}
				onPointerDown={stopToolbarEvent}
				onClick={stopToolbarEvent}
				style={{ fontSize: 13, height: 32, border: "none", background: "transparent", outline: "none", cursor: "pointer", color: "#1e293b", width: 108, maxWidth: 108, flexShrink: 0, fontFamily: "inherit" }}
			>
				{(fontOptions || FONT_OPTIONS).map((f) => (
					<option key={f} value={f} style={{ fontFamily: f }}>{f.split(",")[0]}</option>
				))}
			</select>
			{sep()}
			<Box display="flex" alignItems="center" style={{ gap: 2 }} data-er-editor-ui="1">
				<IconButton
					size="small"
					onClick={() => onPatch({ fontSize: Math.max(8, (Number(s.fontSize) || defaultFontSize) - 2) })}
					style={{ padding: 4 }}
					onMouseDown={stopToolbarEvent}
					onPointerDown={stopToolbarEvent}
				>
					<Box fontSize={14} color="#64748b">−</Box>
				</IconButton>
				<input
					type="text"
					inputMode="numeric"
					value={fontSizeDraft !== null ? fontSizeDraft : String(fsBase)}
					onFocus={() => setFontSizeDraft(String(fsBase))}
					onChange={(e) => setFontSizeDraft(e.target.value.replace(/[^\d]/g, ""))}
					onBlur={() => {
						const t = fontSizeDraft;
						setFontSizeDraft(null);
						if (t === null || t === "") return;
						const n = parseInt(t, 10);
						if (!Number.isNaN(n)) onPatch({ fontSize: Math.max(8, Math.min(500, n)) });
					}}
					onMouseDown={stopToolbarEvent}
					onPointerDown={stopToolbarEvent}
					onClick={stopToolbarEvent}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							e.stopPropagation();
							e.currentTarget.blur();
						}
					}}
					style={{ width: 36, height: 28, fontSize: 13, textAlign: "center", border: "none", outline: "none", background: "transparent", color: "#1e293b" }}
				/>
				<IconButton
					size="small"
					onClick={() => onPatch({ fontSize: Math.min(500, (Number(s.fontSize) || defaultFontSize) + 2) })}
					style={{ padding: 4 }}
					onMouseDown={stopToolbarEvent}
					onPointerDown={stopToolbarEvent}
				>
					<Box fontSize={14} color="#64748b">+</Box>
				</IconButton>
			</Box>
			{sep()}
			<Box
				flexShrink={0}
				data-er-editor-ui="1"
				onMouseDown={(e) => {
					stopToolbarEvent(e);
					if (onColorPickerPointerDown) onColorPickerPointerDown();
				}}
				onPointerDown={(e) => {
					stopToolbarEvent(e);
					if (onColorPickerPointerDown) onColorPickerPointerDown();
				}}
			>
				<RgbaColorPickerPopover compact value={colorValue || "rgba(0,0,0,1)"} onChange={onColorChange} />
			</Box>
			{sep()}
			<Tooltip title="Negrita">{tbBtn(() => onPatch({ fontWeight: s.fontWeight === "bold" ? "normal" : "bold" }), FormatBoldIcon, s.fontWeight === "bold")}</Tooltip>
			<Tooltip title="Cursiva">{tbBtn(() => onPatch({ fontStyle: s.fontStyle === "italic" ? "normal" : "italic" }), FormatItalicIcon, s.fontStyle === "italic")}</Tooltip>
			<Tooltip title="Subrayado">{tbBtn(() => onPatch({ textDecoration: s.textDecoration === "underline" ? "none" : "underline" }), FormatUnderlinedIcon, s.textDecoration === "underline")}</Tooltip>
			{sep()}
			<Tooltip title="Alinear a la izquierda">{tbBtn(() => onPatch({ textAlign: "left" }), FormatAlignLeftIcon, (s.textAlign || "left") === "left")}</Tooltip>
			<Tooltip title="Centrar">{tbBtn(() => onPatch({ textAlign: "center" }), FormatAlignCenterIcon, s.textAlign === "center")}</Tooltip>
			<Tooltip title="Alinear a la derecha">{tbBtn(() => onPatch({ textAlign: "right" }), FormatAlignRightIcon, s.textAlign === "right")}</Tooltip>
			<Tooltip title="Justificar">{tbBtn(() => onPatch({ textAlign: "justify" }), FormatAlignJustifyIcon, s.textAlign === "justify")}</Tooltip>
			{showDuplicate ? (
				<>
					{sep()}
					<Tooltip title="Duplicar">
						<IconButton
							size="small"
							data-er-editor-ui="1"
							onClick={(e) => {
								e.stopPropagation();
								if (onDuplicate) onDuplicate();
							}}
							style={{ padding: 5 }}
							onMouseDown={stopToolbarEvent}
							onPointerDown={stopToolbarEvent}
						>
							<ControlPointDuplicateIcon style={{ fontSize: 20, color: "#64748b" }} />
						</IconButton>
					</Tooltip>
				</>
			) : null}
		</Box>
	);
}

function TextFloatingToolbar({ element, onUpdate, onDuplicate, onRemove, fontOptions }) {
	const s = element?.style || {};
	const savedSelectionRef = useRef(null);
	const handleColorChange = (c) => {
		applyTextColorToElement(element, c, savedSelectionRef.current, onUpdate);
		savedSelectionRef.current = saveTextSelection();
	};
	return (
		<StyleFloatingToolbarBase
			anchor={element}
			values={s}
			onPatch={(patch) => onUpdate({ ...element, style: { ...s, ...patch } })}
			colorValue={s.color || "rgba(0,0,0,1)"}
			onColorChange={handleColorChange}
			onColorPickerPointerDown={() => {
				savedSelectionRef.current = saveTextSelection();
			}}
			fontOptions={fontOptions}
			onDuplicate={onDuplicate}
			defaultFontSize={18}
		/>
	);
}

function QuizOptionsFloatingToolbar({ element, onUpdate, fontOptions }) {
	const qStyle = element?.content?.quizOptionsStyle || {};
	const patchStyle = (patch) =>
		onUpdate({
			...element,
			content: { ...element.content, quizOptionsStyle: { ...qStyle, ...patch } },
		});
	return (
		<StyleFloatingToolbarBase
			anchor={element}
			values={qStyle}
			onPatch={patchStyle}
			colorValue={qStyle.color || "#334155"}
			onColorChange={(c) => patchStyle({ color: c })}
			fontOptions={fontOptions}
			showDuplicate={false}
			defaultFontSize={12}
		/>
	);
}


export { QuizPreviewThumbnail, DraftIntTextField, TextFloatingToolbar, QuizOptionsFloatingToolbar };
